import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetServerSideProps } from 'next';
import { toSiteCorePath, withBasePath } from '@src/common/path-convert';
import { AppLanguage } from '@src/common/app-language';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { sitecoreClient, withOps } from '@src/utils/graphql-request';
import {
    CategoriesFragment,
    ContentBlockFragment,
    KolProfileBaseFragment,
    SectionFragment,
} from '@graphqlTypes/sitecore';
import { filterContentBlocks, OrderContentBlock } from '@src/common/filter';
import { sortContentBlocksByScores } from '@src/common/scores/sort-contents';
import { findCategoryTitleByKey, isTimeFrame, objFilter } from '@src/utils/util';
import SectionSorting from '@components/section-sorting/SectionSorting';
import { useGlobalStore } from '@src/hooks/use-global-store';
import { observer } from 'mobx-react-lite';
import cn from 'classnames';
import Open3in1App from '@components/open-3in1-app/Open3in1App';
import { ErrorPageType } from '@src/common/enums';
import { useFilterState } from '@src/hooks/use-section-list-filter';
import FilterKolTabs from '@components/filter-kol-tabs/FilterKolTabs';
import FilterSectionListTabs from '@components/filter-section-list-tabs/FilterSectionListTabs';

interface Props {
    currentPath: string;
    sectionData: { Item: SectionFragment };
    categoryData: CategoriesFragment[];
    kolDataList: KolProfileBaseFragment[];
    /** 是否kol板块，若是kol板块显示KOL filter */
    isKolSection: boolean;
}

const SeeAll = observer((props: Props) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const { currentPath, sectionData, categoryData, isKolSection, kolDataList } = props;
    const { shareDataStore, baseStore } = useGlobalStore();

    const isZHCN = sectionData?.Item?.useSimplifiedChinese?.boolValue ?? false;
    const {
        selectedFilter,
        filteredList: rawFilteredList,
        handleFilterClick,
        displayArticles,
    } = useFilterState({
        filterButtons: sectionData?.Item?.filterButtons?.targetItems ?? [],
        contentBlocks: sectionData?.Item?.contentBlocks ?? [],
        displayArticles: sectionData?.Item?.displayArticles?.targetItems ?? [],
    });

    const filteredList = useMemo(() => rawFilteredList, [rawFilteredList]);

    const [sectionList, setSectionList] = useState([] as ContentBlockFragment[]);
    const [pBNum, setPBNum] = useState(0);
    const [sectionHeight, setSectionHeight] = useState(0);

    const scoringConfig = baseStore?.data?.contentScoringConfig;
    const categoryScores = shareDataStore?.categoryScores;
    const hashtagScores = shareDataStore?.hashtagScores;

    const dataSorting = useCallback(
        (key: string) => {
            if (!key || !filteredList.length) {
                setSectionList(filteredList);
                return;
            }
            const contentData = [...filteredList];
            if (key === 'Newest') {
                const sorted = contentData.sort(
                    (a, b) =>
                        (b?.startDatetime?.dateValue as number) -
                        (a?.startDatetime?.dateValue as number)
                );
                setSectionList(sorted);
            } else if (key === 'Relevance') {
                const scored = sortContentBlocksByScores(
                    contentData,
                    categoryScores,
                    hashtagScores,
                    scoringConfig || {}
                );
                const result = OrderContentBlock(scored, displayArticles);
                setSectionList(result);
            } else {
                setSectionList(filteredList);
            }
        },
        [filteredList, displayArticles, categoryScores, hashtagScores, scoringConfig]
    );
    useEffect(() => {
        const savedKey = sessionStorage.getItem('hasTagSortingKey') || 'Relevance';
        dataSorting(savedKey);
    }, [filteredList, dataSorting]);

    const handleFilterAndResetSorting = useCallback(
        (btn: any) => {
            handleFilterClick(btn);
            const key = 'Relevance';
            sessionStorage.setItem('hasTagSortingKey', key);
            dataSorting(key);
        },
        [handleFilterClick, dataSorting]
    );

    useEffect(() => {
        sessionStorage.setItem('playerLiveId', '[]');
        sessionStorage.setItem('playerId', '[]');
    }, []);

    useEffect(() => {
        const updateHeight = () => {
            if (sectionRef.current) {
                setSectionHeight(sectionRef.current.offsetHeight);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [sectionList.length]);

    return (
        <div className={cn('pl-[17px] pr-[17px]')} style={{ maxWidth: '1440px', margin: '0 auto' }}>
            <div className='fixed top-0 z-20 w-full bg-[#f6f7f8]' ref={sectionRef}>
                <div
                    className='flex justify-between'
                    style={{ width: 'calc(100% - 34px)', maxWidth: '1405px' }}
                >
                    <div
                        className='text-[18px] pt-[12px] pb-[12px]'
                        dangerouslySetInnerHTML={{
                            __html: props?.sectionData?.Item?.sectionTitle?.value || '',
                        }}
                    ></div>
                    <SectionSorting
                        submitSorting={(key) => {
                            dataSorting(key);
                        }}
                        top={`${sectionHeight}`}
                        isZHCN={isZHCN}
                    />
                </div>
                <div>
                    {isKolSection ? (
                        <FilterKolTabs kolDataList={kolDataList} isSharePage />
                    ) : sectionData?.Item?.filterEnabled?.boolValue ? (
                        <FilterSectionListTabs
                            isSharePage
                            filterButtonsList={sectionData?.Item?.filterButtons?.targetItems ?? []}
                            filterButtonClk={handleFilterAndResetSorting}
                            selectedFilter={selectedFilter}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            </div>

            <div
                className='overflow-y-auto'
                style={{ marginTop: `${sectionHeight + 10}px`, paddingBottom: `${pBNum + 50}px` }}
                ref={contentRef}
            >
                <div className='md:grid md:gap-4 md:grid-cols-2'>
                    {sectionList.map((i: ContentBlockFragment, index: number) => {
                        const isDeepLink = i.blockType?.value === 'DeepLink';
                        const categoryTitle =
                            (i?.category?.categories &&
                                i.category.categories.length > 0 &&
                                objFilter(i?.category?.categories)[0]?.categoryTitle?.value) ||
                            '';
                        const categoryName =
                            (i?.category?.categories &&
                                i.category.categories.length > 0 &&
                                objFilter(i?.category?.categories)[0]?.name) ||
                            '';
                        const categoryText = isZHCN
                            ? findCategoryTitleByKey(categoryName, categoryData)
                            : categoryTitle;

                        return (
                            <div key={index} className={cn('pointer-events-none')}>
                                {isDeepLink ? (
                                    <div className='relative'>
                                        <a href={''} target='_blank'>
                                            <div
                                                className={`h-[120px] rounded-[4px] bg-[#fff] p-[10px] relative flex mb-[10px] md:mb-0`}
                                            >
                                                <>
                                                    {isTimeFrame(
                                                        (i.isIconDisplay &&
                                                            i.isIconDisplay?.boolValue) ||
                                                            false,
                                                        i.iconDisplayStartDate?.dateValue || 0,
                                                        i.iconDisplayEndDate?.dateValue || 0
                                                    ) && (
                                                        <img
                                                            src={
                                                                i.iconDisplay?.targetItem?.iconImage
                                                                    ?.src
                                                            }
                                                            alt=''
                                                            loading='lazy'
                                                            className='h-[20px] absolute top-[15px] left-[15px]'
                                                        ></img>
                                                    )}
                                                    <img
                                                        className='w-[100px] h-[100px] rounded-[4px] object-cover'
                                                        src={i?.heroImageSq?.src}
                                                        alt=''
                                                        loading='lazy'
                                                    />
                                                </>
                                                <div className='flex flex-col flex-[1] justify-start pl-[12px] break-all'>
                                                    <div
                                                        className='text-[13px] mb-[2px]  text-[#031E6C] categoryBr line-clamp-1'
                                                        dangerouslySetInnerHTML={{
                                                            __html: categoryText,
                                                        }}
                                                    ></div>
                                                    <div
                                                        className='text-[15px] line-clamp-4 align-bottom leading-[1.27] min-h-[78px]'
                                                        style={{ wordBreak: 'break-word' }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: i.pageTitle?.value || '',
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </a>
                                        {i.shareIcon?.boolValue && (
                                            <img
                                                className='w-[20px] h-[22px] z-10 absolute top-[10px] right-[10px]'
                                                src={withBasePath('/images/share_blue.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className={`h-[120px] rounded-[4px] bg-[#fff] p-[10px] relative flex mb-[10px] md:mb-0`}
                                    >
                                        <>
                                            {isTimeFrame(
                                                (i.isIconDisplay && i.isIconDisplay?.boolValue) ||
                                                    false,
                                                i.iconDisplayStartDate?.dateValue || 0,
                                                i.iconDisplayEndDate?.dateValue || 0
                                            ) && (
                                                <img
                                                    src={i.iconDisplay?.targetItem?.iconImage?.src}
                                                    alt=''
                                                    loading='lazy'
                                                    className='h-[20px] absolute top-[15px] left-[15px]'
                                                ></img>
                                            )}
                                            <img
                                                className='w-[100px] h-[100px] rounded-[4px] object-cover'
                                                src={i?.heroImageSq?.src}
                                                alt=''
                                                loading='lazy'
                                            />
                                        </>
                                        <div className='flex flex-col flex-[1] justify-start pl-[12px] break-all'>
                                            <div
                                                className='text-[13px] mb-[2px]  text-[#031E6C] categoryBr line-clamp-1'
                                                dangerouslySetInnerHTML={{
                                                    __html: categoryText,
                                                }}
                                            ></div>
                                            <div
                                                className='text-[15px] line-clamp-4 align-bottom leading-[1.27] min-h-[78px]'
                                                style={{ wordBreak: 'break-word' }}
                                                dangerouslySetInnerHTML={{
                                                    __html: i.pageTitle?.value || '',
                                                }}
                                            ></div>
                                        </div>
                                        {i.shareIcon?.boolValue && (
                                            <img
                                                className='w-[20px] h-[22px] z-10 ml-1'
                                                src={withBasePath('/images/share_blue.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <Open3in1App currentPath={currentPath} getOverLayHeight={(v) => setPBNum(v)} />
        </div>
    );
});

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const section = toSiteCorePath(ctx.params?.section);
    const locale = ctx.locale || AppLanguage.en_us;

    const [sectionData, kolDataList] = await Promise.all([
        withOps('share-sectionList-contentBlock', () =>
            sitecoreClient.contentBlocksQuery({ path: section, lang: locale })
        ),
        withOps('share-sectionList-kolDataList', () =>
            sitecoreClient.kolDataListQuery({ lang: locale })
        ),
    ]);

    if (!sectionData.Item) {
        return {
            props: {
                errorPageType: ErrorPageType.noSharePage,
                ...(await serverSideTranslations(locale, ['common', 'share'])),
            },
        };
    }
    filterContentBlocks(sectionData.Item, ctx.query.seg, ctx.query.ac);
    const CategoryItem = await withOps('share-sectionList-CategoryItem', () =>
        sitecoreClient.CategoriesQuery({ lang: 'zh-CN' })
    );
    /** 简体中文category data */
    const categoryData = CategoryItem?.Item?.data ?? [];
    const isKolSection = ctx.params?.section === 'kol-section';
    const filterkolDataList =
        kolDataList.kolItem?.kolProfiles && kolDataList.kolItem?.kolProfiles.length > 0
            ? kolDataList.kolItem?.kolProfiles.filter((i) => i.profileEnabled?.boolValue)
            : [];

    return {
        props: {
            currentPath: ctx.params?.section || '',
            sectionData,
            categoryData,
            isKolSection,
            kolDataList: filterkolDataList,
            ...(await serverSideTranslations(ctx.locale || AppLanguage.en_us, [
                'common',
                'home',
                'share',
            ])),
        },
    };
};

export default SeeAll;
